import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import {
  reportSettingsApi,
  type ReportSchedule,
  type ReportScheduleFrequency,
} from "../services/report-settings.api";
import styles from "./ReportScheduleModal.module.scss";

interface ReportScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  reportKey: string;
  reportTitle: string;
  /** Zamanlamaya kaydedilecek mevcut filtreler. */
  currentFilters: Record<string, unknown>;
}

const FREQUENCY_DAILY: ReportScheduleFrequency = 0;
const FREQUENCY_WEEKLY: ReportScheduleFrequency = 1;
const FREQUENCY_MONTHLY: ReportScheduleFrequency = 2;

/**
 * Zamanlanmış e-posta raporu yönetimi (R11).
 *
 * Raporu düzenli almak için kullanıcının ekrana girip elle indirmesi gerekiyordu.
 * Bu modal, mevcut filtrelerle günlük/haftalık/aylık gönderim tanımlar; backend
 * saat başı çalışan bir cron ile raporu üretip alıcılara ek dosya olarak yollar.
 */
const ReportScheduleModal: React.FC<ReportScheduleModalProps> = ({
  isOpen,
  onClose,
  reportKey,
  reportTitle,
  currentFilters,
}) => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState<ReportSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [name, setName] = useState("");
  const [recipients, setRecipients] = useState("");
  const [frequency, setFrequency] = useState<ReportScheduleFrequency>(FREQUENCY_WEEKLY);
  const [hour, setHour] = useState("8");
  const [weekday, setWeekday] = useState("1");
  const [dayOfMonth, setDayOfMonth] = useState("1");
  const [format, setFormat] = useState<"excel" | "pdf">("excel");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setIsLoading(true);
    reportSettingsApi
      .listSchedules()
      .then((items) => setSchedules(items.filter((s) => s.reportKey === reportKey)))
      .catch(() => setSchedules([]))
      .finally(() => setIsLoading(false));
  }, [reportKey]);

  useEffect(() => {
    if (!isOpen) return;
    setName(reportTitle);
    setError(null);
    load();
  }, [isOpen, reportTitle, load]);

  const frequencyOptions = [
    { label: t("reportSettings.frequencyDaily"), value: String(FREQUENCY_DAILY) },
    { label: t("reportSettings.frequencyWeekly"), value: String(FREQUENCY_WEEKLY) },
    { label: t("reportSettings.frequencyMonthly"), value: String(FREQUENCY_MONTHLY) },
  ];

  const weekdayOptions = [
    t("reportSettings.sunday"),
    t("reportSettings.monday"),
    t("reportSettings.tuesday"),
    t("reportSettings.wednesday"),
    t("reportSettings.thursday"),
    t("reportSettings.friday"),
    t("reportSettings.saturday"),
  ].map((label, index) => ({ label, value: String(index) }));

  const hourOptions = Array.from({ length: 24 }, (_, h) => ({
    label: `${String(h).padStart(2, "0")}:00`,
    value: String(h),
  }));

  // Ayın 29-31'i her ayda bulunmadığı için üst sınır 28; aksi halde o aylarda
  // gönderim hiç tetiklenmezdi.
  const dayOptions = Array.from({ length: 28 }, (_, i) => ({
    label: String(i + 1),
    value: String(i + 1),
  }));

  const parseRecipients = (raw: string): string[] =>
    raw
      .split(/[,;\s]+/)
      .map((v) => v.trim())
      .filter(Boolean);

  const handleCreate = async () => {
    const list = parseRecipients(recipients);
    if (!name.trim()) {
      setError(t("reportSettings.errorNameRequired"));
      return;
    }
    if (list.length === 0) {
      setError(t("reportSettings.errorRecipientsRequired"));
      return;
    }
    const invalid = list.filter((v) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v));
    if (invalid.length > 0) {
      setError(t("reportSettings.errorInvalidEmail", { email: invalid[0] }));
      return;
    }

    setError(null);
    setIsSaving(true);
    try {
      await reportSettingsApi.createSchedule({
        reportKey,
        name: name.trim(),
        format,
        frequency,
        hour: Number(hour),
        ...(frequency === FREQUENCY_WEEKLY && { weekday: Number(weekday) }),
        ...(frequency === FREQUENCY_MONTHLY && { dayOfMonth: Number(dayOfMonth) }),
        recipients: list,
        filters: currentFilters,
      });
      toast.success(t("reportSettings.scheduleCreated"));
      setRecipients("");
      load();
    } catch {
      /* hata toast'ı interceptor tarafından gösterilir */
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (schedule: ReportSchedule) => {
    await reportSettingsApi.updateSchedule(schedule.id, { isActive: !schedule.isActive }).catch(() => undefined);
    load();
  };

  const handleDelete = async (schedule: ReportSchedule) => {
    await reportSettingsApi.deleteSchedule(schedule.id).catch(() => undefined);
    toast.success(t("reportSettings.scheduleDeleted"));
    load();
  };

  const describe = (schedule: ReportSchedule): string => {
    const time = `${String(schedule.hour).padStart(2, "0")}:00`;
    if (schedule.frequency === FREQUENCY_DAILY) {
      return t("reportSettings.summaryDaily", { time });
    }
    if (schedule.frequency === FREQUENCY_WEEKLY) {
      const label = weekdayOptions[schedule.weekday ?? 0]?.label ?? "";
      return t("reportSettings.summaryWeekly", { weekday: label, time });
    }
    return t("reportSettings.summaryMonthly", { day: schedule.dayOfMonth ?? 1, time });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("reportSettings.scheduleTitle")} size="lg">
      <div className={styles.body}>
        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>{t("reportSettings.scheduleNew")}</h4>
          <p className={styles.sectionHint}>{t("reportSettings.scheduleHint")}</p>

          <div className={styles.grid}>
            <Input
              label={t("reportSettings.scheduleName")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              required
            />
            <Dropdown
              label={t("reportSettings.frequency")}
              options={frequencyOptions}
              value={String(frequency)}
              selectedLabel={frequencyOptions.find((o) => o.value === String(frequency))?.label}
              onSelect={(v) =>
                setFrequency(
                  Number(Array.isArray(v) ? v[0] : v) as ReportScheduleFrequency,
                )
              }
            />
            <Dropdown
              label={t("reportSettings.hour")}
              options={hourOptions}
              value={hour}
              selectedLabel={hourOptions.find((o) => o.value === hour)?.label}
              onSelect={(v) => setHour(Array.isArray(v) ? (v[0] ?? "8") : v)}
            />
            {frequency === FREQUENCY_WEEKLY && (
              <Dropdown
                label={t("reportSettings.weekday")}
                options={weekdayOptions}
                value={weekday}
                selectedLabel={weekdayOptions.find((o) => o.value === weekday)?.label}
                onSelect={(v) => setWeekday(Array.isArray(v) ? (v[0] ?? "1") : v)}
              />
            )}
            {frequency === FREQUENCY_MONTHLY && (
              <Dropdown
                label={t("reportSettings.dayOfMonth")}
                options={dayOptions}
                value={dayOfMonth}
                selectedLabel={dayOptions.find((o) => o.value === dayOfMonth)?.label}
                onSelect={(v) => setDayOfMonth(Array.isArray(v) ? (v[0] ?? "1") : v)}
              />
            )}
            <Dropdown
              label={t("reportSettings.format")}
              options={[
                { label: "Excel", value: "excel" },
                { label: "PDF", value: "pdf" },
              ]}
              value={format}
              selectedLabel={format === "pdf" ? "PDF" : "Excel"}
              onSelect={(v) => setFormat((Array.isArray(v) ? v[0] : v) === "pdf" ? "pdf" : "excel")}
            />
          </div>

          <Input
            label={t("reportSettings.recipients")}
            placeholder={t("reportSettings.recipientsPlaceholder")}
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            error={!!error}
            errorMessage={error ?? undefined}
            required
          />

          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleCreate}
            disabled={isSaving}
          >
            {t("reportSettings.scheduleCreate")}
          </button>
        </section>

        <section className={styles.section}>
          <h4 className={styles.sectionTitle}>{t("reportSettings.scheduleExisting")}</h4>
          {isLoading && <p className={styles.sectionHint}>{t("common.loading")}</p>}
          {!isLoading && schedules.length === 0 && (
            <p className={styles.sectionHint}>{t("reportSettings.scheduleEmpty")}</p>
          )}
          <ul className={styles.list}>
            {schedules.map((schedule) => (
              <li key={schedule.id} className={styles.listItem}>
                <div className={styles.listMain}>
                  <span className={styles.listName}>{schedule.name}</span>
                  <span className={styles.listMeta}>
                    {describe(schedule)} · {schedule.format.toUpperCase()} ·{" "}
                    {schedule.recipients.join(", ")}
                  </span>
                  {schedule.lastError && (
                    <span className={styles.listError}>
                      {t("reportSettings.lastError", { error: schedule.lastError })}
                    </span>
                  )}
                </div>
                <div className={styles.listActions}>
                  <button type="button" onClick={() => handleToggleActive(schedule)}>
                    {schedule.isActive ? t("reportSettings.pause") : t("reportSettings.resume")}
                  </button>
                  <button
                    type="button"
                    className={styles.deleteButton}
                    onClick={() => handleDelete(schedule)}
                  >
                    {t("buttons.delete")}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Modal>
  );
};

export default ReportScheduleModal;
